import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, ArrowLeft, FolderOpen, FileText, MessageSquare,
  ChevronDown, ChevronUp, CheckCircle2, Circle, Download,
  Send, Calendar, Lock, X, Check, Search
} from 'lucide-react';
import { projectsAPI, messagesAPI } from '../services/api';
import './EngineerDashboard.css';

const STAGES = [
  'Στάδιο Παραγγελίας', 'Στάδιο Βαφείου', 'Στάδιο Παραλαβής Υλικών',
  'Στάδιο Εκφόρτωσης', 'Στάδιο Μέτρα Κοπής', 'Στάδιο Παραγωγής',
  'Στάδιο Συσκευασίας', 'Στάδιο Εγκατάστασης', 'Ολοκληρωμένο'
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);
  const [projectProgress, setProjectProgress] = useState({});
  const [projectFiles, setProjectFiles] = useState({});
  const [projectMessages, setProjectMessages] = useState({});
  const [activeSubTab, setActiveSubTab] = useState('details');
  const [newMessage, setNewMessage] = useState('');
  const [searchCode, setSearchCode] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    if (!storedUser) {
      navigate('/login');
    } else if (storedUser.role === 'admin') {
      navigate('/admin');
    } else if (storedUser.role === 'engineer') {
      navigate('/engineer');
    } else {
      setUser(storedUser);
      loadProjects();
    }
  }, [navigate]);

  const loadProjects = async () => {
    try {
      const res = await projectsAPI.getAll();
      setProjects(res.data);
    } catch (e) {
      console.error('Error loading projects:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const toggleProject = async (project) => {
    if (expandedProject === project.id) {
      setExpandedProject(null);
      return;
    }
    setExpandedProject(project.id);
    setActiveSubTab('details');
    try {
      const [pr, fr, mr] = await Promise.all([
        projectsAPI.getProgress(project.id),
        projectsAPI.getFiles(project.id),
        messagesAPI.getByProject(project.id)
      ]);
      setProjectProgress(prev => ({ ...prev, [project.id]: pr.data }));
      setProjectFiles(prev => ({ ...prev, [project.id]: fr.data }));
      setProjectMessages(prev => ({ ...prev, [project.id]: mr.data }));
    } catch (e) {
      console.error(e);
    }
  };

  const isStageCompleted = (projectId, stage) => {
    const list = projectProgress[projectId] || [];
    return list.find(p => p.status === stage)?.completed || false;
  };

  const handleAcceptOffer = async (projectId) => {
    if (!window.confirm('Είστε σίγουρος για την αποδοχή της προσφοράς; Η ενέργεια αυτή δεν αναιρείται.')) return;
    try {
      await projectsAPI.acceptOffer(projectId);
      loadProjects();
    } catch (e) {
      alert(e.response?.data?.detail || 'Σφάλμα');
    }
  };

  const handleRejectOffer = async () => {
    if (!showRejectModal) return;
    try {
      await projectsAPI.rejectOffer(showRejectModal, rejectReason);
      setShowRejectModal(null);
      setRejectReason('');
      loadProjects();
    } catch (e) {
      alert(e.response?.data?.detail || 'Σφάλμα');
    }
  };

  const handleSendMessage = async (projectId) => {
    if (!newMessage.trim()) return;
    try {
      await messagesAPI.send({ project_id: projectId, content: newMessage });
      setNewMessage('');
      const r = await messagesAPI.getByProject(projectId);
      setProjectMessages(prev => ({ ...prev, [projectId]: r.data }));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) { alert('Σφάλμα'); }
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return '#';
    // Convert /app/uploads/... to /api/uploads/...
    const idx = filePath.indexOf('/uploads/');
    if (idx >= 0) return `${BACKEND_URL}/api${filePath.substring(idx)}`;
    return filePath;
  };

  const getDeliveryInfo = (deliveryDate) => {
    if (!deliveryDate) return null;
    const days = Math.ceil((new Date(deliveryDate) - new Date()) / 86400000);
    if (days <= 0) return { color: '#666', label: 'Εκπρόθεσμο' };
    if (days <= 30) return { color: '#9C27B0', label: `${days} μέρες` };
    if (days <= 60) return { color: '#f44336', label: `${days} μέρες` };
    if (days <= 90) return { color: '#FF9800', label: `${days} μέρες` };
    return { color: '#4CAF50', label: `${days} μέρες` };
  };

  const filtered = projects.filter(p =>
    !searchCode ||
    p.project_code.toLowerCase().includes(searchCode.toLowerCase()) ||
    p.title.toLowerCase().includes(searchCode.toLowerCase())
  );

  if (!user) return null;

  return (
    <div className="engineer-dashboard" data-testid="customer-dashboard">
      <header className="engineer-header">
        <button className="back-to-home-btn" onClick={() => navigate('/')} aria-label="Επιστροφή στην αρχική">
          <ArrowLeft size={20} /><span>Αρχική</span>
        </button>
        <h1>Πύλη Πελάτη</h1>
        <div className="user-info">
          <span>{user.full_name || user.username} {user.customer_number && `(${user.customer_number})`}</span>
          <button className="logout-btn" onClick={handleLogout} data-testid="customer-logout">
            <LogOut size={18} /><span>Αποσύνδεση</span>
          </button>
        </div>
      </header>

      <main className="engineer-content">
        <div className="admin-section">
          <div className="section-header">
            <h2><FolderOpen size={24} /> Οι Προσφορές & Έργα μου</h2>
          </div>

          <div style={{ marginBottom: '1.5rem', maxWidth: 400, position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input type="text" value={searchCode} onChange={(e) => setSearchCode(e.target.value)}
              placeholder="Αναζήτηση κωδικού ή τίτλου..."
              style={{ width: '100%', padding: '0.875rem 0.875rem 0.875rem 2.5rem', border: '1px solid #d0d0d0', fontSize: '1rem', fontFamily: 'inherit' }}
              data-testid="search-projects"
            />
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', padding: '3rem' }}>Φόρτωση...</p>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <FolderOpen size={48} />
              <p>Δεν υπάρχουν προσφορές/έργα ακόμα.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {filtered.map(project => {
                const deliveryInfo = getDeliveryInfo(project.delivery_date);
                return (
                  <div key={project.id} style={{ border: '1px solid #e0e0e0', background: '#fff', borderLeft: deliveryInfo ? `4px solid ${deliveryInfo.color}` : '1px solid #e0e0e0' }}
                    data-testid={`customer-project-${project.project_code}`}
                  >
                    <div onClick={() => toggleProject(project)}
                      style={{ padding: '1rem 1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', background: expandedProject === project.id ? '#f5f5ff' : 'transparent' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, flexWrap: 'wrap', minWidth: 0 }}>
                        <span className="project-code">{project.project_code}</span>
                        <span style={{ fontWeight: 500, color: '#1a1a1a' }}>{project.title}</span>
                        {project.offer_accepted && (
                          <span style={{ color: '#4CAF50', fontSize: '0.75rem', background: '#e8f5e9', padding: '0.15rem 0.5rem', display: 'flex', alignItems: 'center', gap: 3, borderRadius: 3 }}>
                            <Lock size={10} /> Αποδεκτή
                          </span>
                        )}
                        {project.offer_rejected && (
                          <span style={{ color: '#d32f2f', fontSize: '0.75rem', background: '#ffebee', padding: '0.15rem 0.5rem', borderRadius: 3 }}>
                            Απορρίφθηκε
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {deliveryInfo && (
                          <span style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontWeight: 500, background: `${deliveryInfo.color}15`, color: deliveryInfo.color, borderRadius: 3 }}>
                            <Calendar size={12} style={{ verticalAlign: 'middle' }} /> {deliveryInfo.label}
                          </span>
                        )}
                        <span style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontWeight: 500, background: '#f0f0f0', color: '#666', borderRadius: 3 }}>
                          {project.status}
                        </span>
                        {expandedProject === project.id ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
                      </div>
                    </div>

                    {expandedProject === project.id && (
                      <div style={{ borderTop: '1px solid #e0e0e0' }}>
                        <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', background: '#fafafa', flexWrap: 'wrap' }}>
                          {[
                            { id: 'details', label: 'Στοιχεία' },
                            { id: 'progress', label: 'Πρόοδος' },
                            { id: 'files', label: 'Αρχεία' },
                            { id: 'chat', label: 'Συνομιλία' },
                          ].map(tab => (
                            <button key={tab.id} onClick={() => setActiveSubTab(tab.id)}
                              style={{
                                padding: '0.75rem 1.25rem', border: 'none',
                                borderBottom: activeSubTab === tab.id ? '2px solid #667eea' : '2px solid transparent',
                                background: 'transparent', color: activeSubTab === tab.id ? '#667eea' : '#666',
                                fontWeight: activeSubTab === tab.id ? 600 : 400, cursor: 'pointer', fontSize: '0.85rem'
                              }}
                              data-testid={`tab-${tab.id}-${project.project_code}`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        <div style={{ padding: '1.5rem' }}>
                          {/* Details */}
                          {activeSubTab === 'details' && (
                            <div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: 600 }}>
                                <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Κωδικός</span><p style={{ margin: '0.2rem 0 0', fontWeight: 500 }}>{project.project_code}</p></div>
                                <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Τίτλος</span><p style={{ margin: '0.2rem 0 0', fontWeight: 500 }}>{project.title}</p></div>
                                {project.address && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Διεύθυνση</span><p style={{ margin: '0.2rem 0 0' }}>{project.address}</p></div>}
                                {project.area && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Περιοχή</span><p style={{ margin: '0.2rem 0 0' }}>{project.area}</p></div>}
                                {project.systems && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Συστήματα</span><p style={{ margin: '0.2rem 0 0' }}>{project.systems}</p></div>}
                                {project.color && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Χρώμα</span><p style={{ margin: '0.2rem 0 0' }}>{project.color}</p></div>}
                                {project.delivery_date && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Παράδοση</span><p style={{ margin: '0.2rem 0 0' }}>{new Date(project.delivery_date).toLocaleDateString('el-GR')}</p></div>}
                                {project.description && <div style={{ gridColumn: '1 / -1' }}><span style={{ fontSize: '0.8rem', color: '#999' }}>Περιγραφή</span><p style={{ margin: '0.2rem 0 0', lineHeight: 1.5 }}>{project.description}</p></div>}
                              </div>

                              {/* Offer Decision */}
                              <div style={{ marginTop: '1.5rem', padding: '1rem 1.25rem', border: '1px solid #e0e0e0', background: '#fafafa' }}>
                                {project.offer_accepted ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <Lock size={20} color="#4CAF50" />
                                    <div>
                                      <p style={{ margin: 0, fontWeight: 600, color: '#2e7d32' }}>Έχετε αποδεχθεί την προσφορά</p>
                                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#666' }}>
                                        {project.offer_accepted_at && new Date(project.offer_accepted_at).toLocaleString('el-GR')}
                                      </p>
                                    </div>
                                  </div>
                                ) : project.offer_rejected ? (
                                  <div>
                                    <p style={{ margin: 0, fontWeight: 600, color: '#d32f2f' }}>Έχετε απορρίψει την προσφορά</p>
                                    {project.offer_rejected_reason && (
                                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#666' }}>Αιτιολογία: {project.offer_rejected_reason}</p>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <p style={{ margin: '0 0 0.75rem', fontWeight: 500 }}>Απαντήστε στην προσφορά:</p>
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                      <button onClick={() => handleAcceptOffer(project.id)} className="btn-primary"
                                        style={{ background: '#4CAF50', border: 'none' }}
                                        data-testid={`accept-${project.project_code}`}
                                      >
                                        <Check size={16} /> Αποδοχή
                                      </button>
                                      <button onClick={() => setShowRejectModal(project.id)}
                                        style={{ padding: '0.6rem 1rem', border: '1px solid #d32f2f', background: '#fff', color: '#d32f2f', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}
                                        data-testid={`reject-${project.project_code}`}
                                      >
                                        <X size={16} /> Απόρριψη
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Progress Timeline */}
                          {activeSubTab === 'progress' && (
                            <div>
                              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>Παρακολουθήστε την εξέλιξη του έργου:</p>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                {STAGES.map(stage => {
                                  const completed = isStageCompleted(project.id, stage);
                                  return (
                                    <div key={stage}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                                        padding: '0.75rem 1rem',
                                        background: completed ? '#e8f5e9' : '#fff',
                                        border: completed ? '1px solid #a5d6a7' : '1px solid #e0e0e0',
                                        borderRadius: 4
                                      }}
                                    >
                                      {completed ? <CheckCircle2 size={20} color="#4caf50" /> : <Circle size={20} color="#bbb" />}
                                      <span style={{ fontSize: '0.9rem', color: completed ? '#2e7d32' : '#999' }}>{stage}</span>
                                      {completed && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#66bb6a' }}>OK</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Files */}
                          {activeSubTab === 'files' && (
                            <div>
                              {(projectFiles[project.id] || []).length === 0 ? (
                                <p style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Δεν υπάρχουν αρχεία ακόμα</p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  {(projectFiles[project.id] || []).map(f => (
                                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', border: '1px solid #e0e0e0', background: '#fafafa' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <FileText size={18} color="#667eea" />
                                        <div>
                                          <p style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem' }}>{f.filename}</p>
                                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#999' }}>{(f.file_size / 1024).toFixed(1)} KB - {new Date(f.uploaded_at).toLocaleDateString('el-GR')}</p>
                                        </div>
                                      </div>
                                      <a href={getFileUrl(f.file_path)} target="_blank" rel="noreferrer"
                                        className="btn-primary" style={{ textDecoration: 'none', fontSize: '0.85rem' }}
                                        data-testid={`download-${f.id}`}
                                      >
                                        <Download size={14} /> Λήψη
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Chat */}
                          {activeSubTab === 'chat' && (
                            <div>
                              <div style={{ height: 320, overflowY: 'auto', border: '1px solid #e0e0e0', padding: '1rem', marginBottom: '1rem', background: '#fafafa' }}>
                                {(projectMessages[project.id] || []).length === 0 ? (
                                  <p style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Δεν υπάρχουν μηνύματα</p>
                                ) : (
                                  (projectMessages[project.id] || []).map(msg => {
                                    const isOwn = msg.sender_id === user.id;
                                    return (
                                      <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', marginBottom: '0.75rem' }}>
                                        <span style={{ fontSize: '0.75rem', color: '#999', marginBottom: 2 }}>
                                          {msg.sender_name} {msg.sender_role === 'admin' && '(Διαχειριστής)'}
                                        </span>
                                        <div style={{ background: isOwn ? '#667eea' : '#fff', color: isOwn ? '#fff' : '#333', padding: '0.5rem 1rem', borderRadius: 12, maxWidth: '70%', border: isOwn ? 'none' : '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                                          {msg.content}
                                        </div>
                                        <span style={{ fontSize: '0.7rem', color: '#bbb', marginTop: 2 }}>
                                          {new Date(msg.created_at).toLocaleString('el-GR')}
                                        </span>
                                      </div>
                                    );
                                  })
                                )}
                                <div ref={messagesEndRef} />
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(project.id)}
                                  placeholder="Γράψτε μήνυμα στον διαχειριστή..."
                                  style={{ flex: 1, padding: '0.75rem', border: '1px solid #d0d0d0', fontSize: '0.9rem', fontFamily: 'inherit' }}
                                  data-testid={`chat-input-${project.project_code}`}
                                />
                                <button onClick={() => handleSendMessage(project.id)} className="btn-primary" data-testid={`chat-send-${project.project_code}`}>
                                  <Send size={18} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay-admin" onClick={() => setShowRejectModal(null)}>
          <div className="modal-admin" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <div className="modal-header-admin">
              <h3>Απόρριψη Προσφοράς</h3>
              <button onClick={() => setShowRejectModal(null)}><X size={20} /></button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <p style={{ marginBottom: '1rem', color: '#666' }}>Παρακαλούμε εξηγήστε τον λόγο απόρριψης (προαιρετικό):</p>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                rows={4} placeholder="Π.χ. Υψηλή τιμή, διαφορετικές απαιτήσεις..."
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #d0d0d0', fontFamily: 'inherit', fontSize: '0.9rem' }}
                data-testid="reject-reason-input"
              />
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowRejectModal(null)}>Ακύρωση</button>
                <button onClick={handleRejectOffer}
                  style={{ padding: '0.6rem 1.25rem', border: 'none', background: '#d32f2f', color: '#fff', cursor: 'pointer' }}
                  data-testid="reject-confirm-btn"
                >Απόρριψη</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
